import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaPlusCircle } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import InputSelectAsync from "../../../../../components/InputSelectAsync";
import { Loading } from "../../../../../components/Loading";
import { BrComerceApiService } from "../../../../../services/BrComerceApiService";
import { intersectionArraysForAdd, intersectionArraysForDeletion } from "../../../../../utils/intersectionArrays";
import useGetWindowWidth from "../../../../../utils/useGetWindowWidth";
import Container from "../../../components/Container";
import { PackageCard } from "../../../components/PackageCard";
import { ScanModal } from "../../../components/ScanModal";
import styles from "./styles.module.scss";
import { MdClear } from "react-icons/md";
import debounce from "../../../../../utils/debounce";

const EditRoutes = () => {
  const api = new BrComerceApiService();
  const history = useHistory();

  const [loading, setLoading] = useState(false);

  const [route, setRoute] = useState({});
  const [routeId, setRouteId] = useState();

  const [driver, setDriver] = useState();
  const [selectedDriver, setSelectedDriver] = useState(null);

  const [routePackages, setRoutePackages] = useState([]);
  const [newRoutePackages, setNewRoutePackages] = useState([]);
  const [deleteRoutePackages, setDeleteRoutePackages] = useState([]);
  const [isOwnCollect, setIsOwnCollect] = useState(false);

  const widthWindows = useGetWindowWidth();
  const [packagesExpand, setPackagesExpand] = useState([]);
  const [addPackageModal, setAddPackageModal] = useState(false);
  const [originalPackages, setOriginalPackages] = useState([]);

  const [packagesFiltered, setPackagesFiltered] = useState([]);
  const [search, setSearch] = useState("");

  const handleFormSubmit = useCallback(
    async e => {
      e.preventDefault();
      let newAddresses;
      let removeAddresses;

      if (newRoutePackages?.length) {
        const objectsToAdd = intersectionArraysForAdd(originalPackages, newRoutePackages);
        newAddresses = objectsToAdd.map((s) => {
          return {
            id_shipping: s?.id_shipping ? s?.id_shipping : s?.id,
            is_own_collect: Boolean(s?.id_own_collect || s?.collectTimeStart || s?.collectTimeEnd || s?.collectFrequency)
          }
        });
      };

      if (deleteRoutePackages?.length) {
        const objectsToDelete = intersectionArraysForDeletion(originalPackages, deleteRoutePackages);
        removeAddresses = objectsToDelete.map((s) => {
          return {
            id_shipping: s?.id_shipping ? s?.id_shipping : s?.id_own_collect,
            is_own_collect: Boolean(s?.id_own_collect || s?.collectTimeStart || s?.collectTimeEnd || s?.collectFrequency)
          }
        })
      }

      try {
        setLoading(true);

        const data = {
          driverSub: selectedDriver?.sub,
          ...(newAddresses?.length && { newAddresses }),
          ...(removeAddresses?.length && { removeAddresses })
        };

        await api.makeHttpRequest({
          method: "PUT",
          url: `/route-admin/${routeId}`,
          data
        });

        Swal.fire({
          title: "Sucesso!",
          text: "Rota editada com sucesso!",
          icon: "success",
          showCancelButton: false,
          confirmButtonText: "Ok",
          confirmButtonColor: "#409E6A"
        }).then(() => {
          history.push("/listagem-rotas");
        });
      } catch (error) {
        const errorMessage = error?.response?.data?.message || "Não foi possível editar a rota!";
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: errorMessage,
          confirmButtonText: "Ok",
          confirmButtonColor: "#CA1818"
        });
      } finally {
        setLoading(false);
      }
    },
    [
      api,
      history,
      deleteRoutePackages,
      newRoutePackages,
      originalPackages,
      selectedDriver?.sub,
      routeId,
      routePackages
    ]
  );

  const handlePackageExpand = useCallback(
    pack => {
      const id = pack?.id_shipping || pack?.id_own_collect;
      const packagesExpandArr = packagesExpand;

      const find = packagesExpandArr.find(p => p.id_shipping === id || p.id_own_collect === id);

      if (find) {
        const newPacks = packagesExpandArr.filter(p => {
          return (p.id_shipping !== id && p.id_own_collect !== id)
        });
        setPackagesExpand([...newPacks]);
      } else {
        packagesExpandArr.push(pack);
        setPackagesExpand([...packagesExpandArr]);
      }
    },
    [packagesExpand]
  );

  const handleRemovePackage = useCallback(
    data => {
      const id = data?.id_own_collect ?? data?.id_shipping ?? data?.id;

      if (routePackages.length === 1) {
        Swal.fire({
          title: "Erro",
          text: "A rota deve conter pelo menos uma entrega",
          icon: "error"
        });
        return;
      };

      Swal.fire({
        title: "Deletar objeto",
        text: "Você realmente deseja deletar este objeto da rota?",
        icon: "warning",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
        confirmButtonText: "Sim, fazer a deleção!",
        reverseButtons: true
      }).then((response) => {
        if (response?.value) {
          const isFromNewPackages = !!newRoutePackages.find(newPack => newPack?.id_shipping === id || newPack?.id_own_collect === id || newPack?.id === id);
          const pack = routePackages.find(pack => isFromNewPackages ? (pack?.id === id && !pack?.id_shipping && !pack?.id_own_collect) : (pack?.id_shipping === id || pack?.id_own_collect === id));

          setDeleteRoutePackages(prevState => [...prevState, pack]);

          setRoutePackages(prevPackages => {
            const filtered = prevPackages.filter(pack => isFromNewPackages
              ? (pack?.id !== id || (!!pack?.id_shipping || !!pack?.id_own_collect))
              : (pack?.id_shipping !== id && pack?.id_own_collect !== id)
            );
            return filtered.map((p, index) => ({ ...p, order: index + 1 }));
          });

          setNewRoutePackages(prevState => prevState?.filter(pack => isFromNewPackages
            ? (pack?.id !== id || (!!pack?.id_shipping || !!pack?.id_own_collect))
            : (pack?.id_shipping !== id && pack?.id_own_collect !== id)));
        }
      })
    },
    [routePackages, newRoutePackages]
  );

  const debouncedFilter = useMemo(() => debounce((searchString, routeShippings, setPackagesFiltered) => {
    const findInBoth = routeShippings.filter(pack => pack?.shipping?.sro?.includes(searchString) || pack?.ownCollect?.id === Number(searchString)).length > 1;

    const packagesFiltered = routeShippings.filter(pack => {
      const filter = (pack?.shipping?.sro?.includes(searchString) && (findInBoth ? /[a-zA-Z]/.test(searchString) : true))
        || pack?.ownCollect?.id === Number(searchString)
        || pack?.shipping?.user?.userData[1]?.Value?.toUpperCase()?.includes(searchString)
        || pack?.ownCollect?.user?.userData[1]?.Value?.toUpperCase()?.includes(searchString);

      return filter
    }
    )

    setPackagesFiltered(packagesFiltered);
  }, 500), []);

  const handleSearch = useCallback(() => {
    const searchString = search.trim().toUpperCase();
    debouncedFilter(searchString, routePackages, setPackagesFiltered);
  }, [routePackages, search, debouncedFilter]);

  useEffect(() => {
    if (!!search?.length) {
      handleSearch(search);
    } else {
      setPackagesFiltered(routePackages);
    }
  }, [handleSearch, search, routePackages])

  useEffect(() => {
    if (routeId) {
      setLoading(true);
      api
        .makeHttpRequest({
          method: "GET",
          url: `route-admin/${routeId}`,
          params: {
            page: 1,
            resultsPerPage: 100,
          }
        })
        .then(response => {
          setRoute(response?.route);
          setLoading(false);
          const routePackages = response?.routePackages?.results;

          setRoutePackages(routePackages);
          setOriginalPackages(routePackages);
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [routeId]);

  useEffect(() => {
    setRouteId(history.location.pathname.split("/")[2]);
    window.setPageTitle("Editar Rota");
  }, [history]);

  useEffect(() => {
    if (route) {
      if (route?.status?.id && route?.status?.id !== 1) {
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: "A rota não pode ser alterada após ser iniciada!",
          confirmButtonText: "Ok",
          confirmButtonColor: "#CA1818"
        }).then(() => history.push("/listagem-rotas"));
      }
      setLoading(true);
      const driver = route?.driver

      if (driver) {
        setDriver({
          label: driver?.name,
          sub: driver?.sub,
          value: driver?.id,
        })
      }

      setLoading(false);
    }
  }, [route]);

  return (
    <>
      <Loading loading={loading} />

      <ScanModal
        setLoading={setLoading}
        packages={routePackages}
        setPackages={setRoutePackages}
        addPackageModal={addPackageModal}
        setAddPackageModal={setAddPackageModal}
        setNewRouteShippings={setNewRoutePackages}
        deleteRouteShippings={deleteRoutePackages}
        setDeleteRouteShippings={setDeleteRoutePackages}
        isOwnCollect={isOwnCollect}
        setIsOwnCollect={setIsOwnCollect}
      />

      <Container title={`Rota n° ${routeId}`}>

        <form className={styles.form} onSubmit={handleFormSubmit}>
          <div className={styles.formContent}>

            {route && <div className={styles.row}>
              <div className={styles.infoCard}>
                <div className={styles.shortInfos}>
                  {route?.status?.name && <div className={styles.info}>
                    <strong>Status:</strong>
                    <span>{route?.status?.name ?? ""}</span>
                  </div>}
                </div>

                <div className={styles.infoAddress}>
                  <div>
                    <strong>Origem: {" "}</strong>
                    <p>{route?.origin_destination?.origin?.address ?? ""}</p>

                  </div>
                  <div>
                    <strong>Destino:</strong>{" "}
                    <p>{route?.origin_destination?.destination?.address ?? ""}</p>
                  </div>
                </div>
              </div>
            </div>
            }

            <div className={`${styles.row} ${styles.driverInput}`}>
              <div className={`${styles.inputContainer}`}>
                <strong>Motorista</strong>
                <div
                  className={`${styles.selectInput}`}
                >
                  <InputSelectAsync
                    userParenting={driver}
                    search={selectedDriver}
                    setSearch={setSelectedDriver}
                    listUserOrDriver={"driver"}
                    placeholder={"Selecione o motorista"}
                  />
                </div>
              </div>
            </div>

            <strong>{`Total de Pacotes ${!!routePackages?.length ? `(${String(routePackages?.length).padStart(2, '0')})` : ""}`}</strong>

            <div className={styles.containerSearch}>

              <div className={`${styles.inputContainer} ${styles.inputContainerSmall}`}>
                <div className={styles.input}>
                  <input
                    type="text"
                    name="search"
                    placeholder="Busque pelo SRO, Cliente ou Coleta"
                    onChange={e => setSearch(e.target.value)}
                    value={search}
                  />

                  {!!search?.length && (
                    <MdClear
                      style={{ cursor: "pointer" }}
                      onClick={() => setSearch("")}
                    />
                  )}
                </div>
              </div>

              <div className={styles.actionsButton}>
                <button
                  type="button"
                  onClick={() => setAddPackageModal(true)}
                  className={`${styles.package}`}
                  disabled={!selectedDriver?.sub}
                >
                  <FaPlusCircle size={16} color="#FFFFFF" />
                  <span>Adicionar pacotes</span>
                </button>
              </div>
            </div>

            <div className={styles.collectWrapper}>
              {!!packagesFiltered?.length ? (

                <div className={styles.packageContainer}>
                  <div className={styles.packagesWrapper}>
                    {packagesFiltered?.map(pack => (
                      <PackageCard
                        widthWindows={widthWindows}
                        key={pack?.id_own_collect ? `ownCollect-${pack.id_own_collect}` : `shipping-${pack.id_shipping}`}
                        shipping={pack?.shipping || pack?.ownCollect || pack}
                        isExpand={packagesExpand.find(
                          packExpand => pack?.id_shipping === packExpand?.id || pack?.id_own_collect === packExpand?.id || pack?.id === packExpand?.id
                        )}
                        onExpand={() => handlePackageExpand(pack)}
                        onDelete={() => handleRemovePackage(pack)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className={styles.notFoundOrEmptyContainer}>
                  <p>
                    {
                      !packagesFiltered?.length && search
                        ? "Não foram encontradas envios para essa pesquisa."
                        : "Não foram encontradas envios para Rota."
                    }
                  </p>
                </div>
              )}

            </div>
          </div>

          <div className={styles.buttonsContainer}>
            <div className={styles.buttonContainerBack}>
              <button type="button" onClick={() => history.push("/listagem-rotas")}>
                Voltar
              </button>
            </div>
            <div className={styles.buttonContainer}>
              <button type="submit" disabled={
                !selectedDriver
                || !routePackages?.length
              }>{"Atualizar"}</button>
            </div>
          </div>
        </form>
      </Container>
    </>
  );
};

export { EditRoutes };